<div class="scanner-container">
  <div class="camapign-holder">
    @if (campaignLoading) {
      <div class="campaign-loading">
        <mat-spinner diameter="24"></mat-spinner>
      </div>
    }
    @if (campaignError) {
      <div class="campaign-error">{{ campaignError }}</div>
    }
    @if (campaign && !campaignLoading) {
      <div class="campaign-info">
        <div class="campaign-info__name">{{ campaign.name }}</div>
        <div class="campaign-info__badges">
          <span class="badge" [class.badge--active]="campaign.active" [class.badge--inactive]="!campaign.active">
            <mat-icon>{{ campaign.active ? 'check_circle' : 'cancel' }}</mat-icon>
            {{ campaign.active ? 'Aktywna' : 'Nieaktywna' }}
          </span>
          @if (campaign.valid) {
            <span class="badge badge--date">
              <mat-icon>event</mat-icon>
              Ważna do: {{ campaign.valid | date:'yyyy-MM-dd' }}
            </span>
          }
        </div>
      </div>
    }
  </div>

  <!-- Manual entry toggle button -->
  @if (!scanDetails && !scanDetailsLoading) {
    <div class="manual-entry-toggle">
      <button mat-raised-button (click)="toggleManualEntry()" class="toggle-btn">
        <mat-icon>{{ manualEntryMode ? 'qr_code_scanner' : 'keyboard' }}</mat-icon>
        {{ manualEntryMode ? 'Skanuj kod QR' : 'Wpisz kod ręcznie' }}
      </button>
    </div>
  }

  <!-- Manual code entry form -->
  @if (manualEntryMode && !scanDetails && !scanDetailsLoading) {
    <div class="manual-entry-form">
      <div class="manual-entry-form__header">
        <mat-icon>edit</mat-icon>
        <span>Wprowadź kod ręcznie</span>
      </div>
      <div class="manual-entry-form__input-group">
        <input
          type="text"
          [value]="manualCode"
          (input)="manualCode = $event.target.value"
          (keydown.enter)="submitManualCode()"
          placeholder="Wpisz kod..."
          class="manual-entry-form__input"
          autofocus
        />
        <button
          mat-raised-button
          color="primary"
          (click)="submitManualCode()"
          [disabled]="!manualCode.trim()"
          class="manual-entry-form__submit">
          <mat-icon>check</mat-icon>
          Potwierdź
        </button>
      </div>
    </div>
  }

  <!-- Camera access error (HTTPS required, permission denied, etc.) -->
  @if (cameraError) {
    <div class="camera-error">
      <mat-icon>no_photography</mat-icon>
      <span>{{ cameraError }}</span>
    </div>
  }

  <!-- Camera view — always in DOM for static ViewChild; hidden after scan completes -->
  <video #video playsinline muted [hidden]="cameraError || scanDetails || scanDetailsLoading || manualEntryMode"></video>
  <canvas #canvas hidden></canvas>

  <!-- Loading while fetching scan details -->
  @if (scanDetailsLoading) {
    <div class="scan-loading">
      <mat-spinner diameter="40"></mat-spinner>
      <span>Przetwarzanie skanu&hellip;</span>
    </div>
  }

  <!-- Scan details success panel -->
  @if (scanDetails && !scanDetailsLoading) {
    <div class="scan-details-panel">
      <div class="scan-details-panel__icon">
        <button mat-raised-button class="scan-again-btn" (click)="restartScan()">
          Skanuj ponownie
        </button>
      </div>
      <div class="scan-details-panel__icon">
        <mat-icon>check_circle</mat-icon>
      </div>
      @if (scanDetails.person_name) {
        <div class="scan-details-panel__row">
          <mat-icon>person</mat-icon>
          <span>{{ scanDetails.person_name }}</span>
        </div>
      }
      @if (scanDetails.total_score !== undefined) {
        <div class="scan-details-panel__row">
          <mat-icon>star</mat-icon>
          <span>Punkty: <strong>{{ scanDetails.total_score }}</strong></span>
        </div>
      }
      @if (scanDetails.last_scan) {
        <div class="scan-details-panel__row">
          <mat-icon>schedule</mat-icon>
          <span>Ostatni skan: {{ scanDetails.last_scan | date:'yyyy-MM-dd HH:mm' }}</span>
        </div>
      }
      @if (scanDetails.is_active !== undefined) {
        <div class="scan-details-panel__row">
          <mat-icon>{{ scanDetails.is_active ? 'verified' : 'block' }}</mat-icon>
          <span [class.text-ok]="scanDetails.is_active" [class.text-err]="!scanDetails.is_active">
            {{ scanDetails.is_active ? 'Karta aktywna' : 'Karta nieaktywna' }}
          </span>
        </div>
      }
      @if (scanDetails.is_active && isCampaignValid && !rewardSelectionVisible) {
        <div class="scan-details-panel__menu">
          <button class="action-btn action-btn--rewards" (click)="openRewardSelection()">
            <mat-icon>card_giftcard</mat-icon>
            <span>Wybór nagród</span>
          </button>
          @if (campaign?.point_per_transaction) {
            <button class="action-btn action-btn--transaction" (click)="registerTransaction()">
              <mat-icon>point_of_sale</mat-icon>
              <span>Zarejestruj transakcję</span>
            </button>
          }
          @if (transactionFormVisible && campaign?.point_per_transaction) {
            <div class="transaction-form">
              <label class="transaction-form__label" for="txAmount">Kwota transakcji (PLN)</label>
              @if (transactionLimits.min !== null || transactionLimits.max !== null) {
                <div class="transaction-form__limits">
                  @if (transactionLimits.min !== null) {
                    <span class="transaction-form__limit">
                      <mat-icon>arrow_upward</mat-icon> Min: {{ transactionLimits.min | number:'1.2-2' }} PLN
                    </span>
                  }
                  @if (transactionLimits.max !== null) {
                    <span class="transaction-form__limit">
                      <mat-icon>arrow_downward</mat-icon> Max: {{ transactionLimits.max | number:'1.2-2' }} PLN
                    </span>
                  }
                </div>
              }
              <div class="transaction-form__row">
                <input
                  id="txAmount"
                  class="transaction-form__input"
                  type="number"
                  [min]="transactionLimits.min ?? 1"
                  [max]="transactionLimits.max ?? undefined"
                  step="1"
                  placeholder="0"
                  [value]="transactionAmount"
                  (input)="transactionAmount = $event.target?.value ? +$event.target?.value : null"
                  (keydown.enter)="submitTransactionForm()"
                  autofocus
                  />
                <button
                  class="transaction-form__submit"
            [disabled]="!transactionAmount || transactionAmount <= 0
              || (transactionLimits.min !== null && transactionAmount < transactionLimits.min)
              || (transactionLimits.max !== null && transactionAmount > transactionLimits.max)"
            (click)="submitTransactionForm()">
                  <mat-icon>check</mat-icon>
                </button>
              </div>
              @if (campaign?.point_per_transaction) {
                <div class="transaction-form__points">
                  @if (calculatedPoints !== null) {
                    <mat-icon>star</mat-icon>
                    <span>Punkty za transakcję: <strong>{{ calculatedPoints | number }}</strong></span>
                  } @else {
                    <mat-icon>star_border</mat-icon>
                    <span>{{ campaign!.point_per_transaction }} pkt / PLN</span>
                  }
                </div>
              }
              @if (transactionAmount && transactionLimits.min !== null && transactionAmount < transactionLimits.min) {
                <div class="transaction-form__warning"
                  >
                  <mat-icon>warning</mat-icon>
                  Kwota minimalna: {{ transactionLimits.min | number:'1.2-2' }} PLN
                </div>
              }
              @if (transactionAmount && transactionLimits.max !== null && transactionAmount > transactionLimits.max) {
                <div class="transaction-form__warning"
                  >
                  <mat-icon>warning</mat-icon>
                  Kwota maksymalna: {{ transactionLimits.max | number:'1.2-2' }} PLN
                </div>
              }
            </div>
          }
          @if (campaign?.point_per_entry) {
            <button class="action-btn action-btn--entry" (click)="registerEntry()">
              <mat-icon>how_to_reg</mat-icon>
              <span>Zarejestruj skan / wejście</span>
            </button>
          }
        </div>
      }
      <!-- Reward selection panel -->
      @if (rewardSelectionVisible) {
        <div class="rewards-panel">
          <div class="rewards-panel__header">
            <mat-icon>card_giftcard</mat-icon>
            <span>Wybór nagrody</span>
            <button mat-icon-button class="rewards-panel__close" (click)="closeRewardSelection()">
              <mat-icon>close</mat-icon>
            </button>
          </div>
          @if (rewardsLoading) {
            <div class="rewards-panel__loading">
              <mat-spinner diameter="36"></mat-spinner>
            </div>
          }
          @if (rewardsError) {
            <div class="rewards-panel__error">
              <mat-icon>error_outline</mat-icon> {{ rewardsError }}
            </div>
          }
          @if (!rewardsLoading && !rewardsError && rewards.length === 0) {
            <div class="rewards-panel__empty">
              <mat-icon>inventory_2</mat-icon>
              <span>Brak dost&#x0119;pnych nagr&#xF3;d</span>
            </div>
          }
          @if (!rewardsLoading && rewards.length > 0) {
            <div class="rewards-list">
              @for (r of rewards; track r) {
                <div
                  class="reward-item"
                  [class.reward-item--disabled]="!canAffordReward(r)"
                  [attr.title]="!canAffordReward(r) ? 'Niewystarczaj\u0105ca liczba punkt\u00f3w' : null"
                  (click)="selectProduct(r)">
                  <div class="reward-item__img">
                    @if (r.file) {
                      <img [src]="imagesUrl + '/partner_products/thumb/' + r.file" alt="{{ r.name }}" />
                    }
                    @if (!r.file) {
                      <mat-icon class="reward-item__no-img">redeem</mat-icon>
                    }
                  </div>
                  <div class="reward-item__body">
                    <strong class="reward-item__name">{{ r.name }}</strong>
                    @if (r.description) {
                      <span class="reward-item__desc">{{ r.description }}</span>
                    }
                    <div class="reward-item__score" [class.reward-item__score--unaffordable]="!canAffordReward(r)">
                      <mat-icon>star</mat-icon>
                      <span>{{ r.score }} pkt</span>
                      @if (!canAffordReward(r)) {
                        <span class="reward-item__score-hint">&#160;(brak punkt\u00f3w)</span>
                      }
                    </div>
                  </div>
                </div>
              }
            </div>
          }
        </div>
      }
    </div>
  }

  <!-- Scan error -->
  @if (scanDetailsErrors.length) {
    <div class="scan-error-panel">
      <div class="scan-error-panel__header">
        <mat-icon>error_outline</mat-icon>
        <span>Błąd serwera</span>
      </div>
      <ul class="scan-error-panel__list">
        @for (msg of scanDetailsErrors; track msg) {
          <li>{{ msg }}</li>
        }
      </ul>
      <button mat-stroked-button (click)="restartScan()">Spróbuj ponownie</button>
    </div>
  }

  <!-- Legacy card-only result (no campaign_id) -->
  @if (scannedResult && !scanDetails) {
    <div class="result-box">
      &#10003; Zeskanowano: {{ scannedCode }}
      <button (click)="restartScan()">Skanuj ponownie</button>
      @if (scannedResult.status === 'ok') {
        <div class="valid-card">Karta jest ważna!</div>
      }
      @if (scannedResult.status !== 'ok') {
        <div class="invalid-card">Karta nieważna: {{ scannedResult.reason }}</div>
      }
    </div>
  }

  <!-- Transaction registration popup -->
  @if (transactionPopupVisible) {
    <div class="transaction-popup-overlay">
      <div class="transaction-popup">
        <!-- Countdown phase -->
        @if (!transactionProcessing && !transactionSuccess && !transactionError) {
          <p class="transaction-popup__name">{{ scanDetails?.person_name }}</p>
          <p class="transaction-popup__subtitle">Rejestracja transakcji za:</p>
          <div class="transaction-popup__ring">
            <svg viewBox="0 0 120 120" width="120" height="120">
              <circle class="ring-bg" cx="60" cy="60" r="54"/>
              <circle class="ring-progress" cx="60" cy="60" r="54"/>
            </svg>
            <span class="transaction-popup__number">{{ transactionCountdown }}</span>
          </div>
          <p class="transaction-popup__amount">{{ transactionAmount | number:'1.2-2' }} PLN</p>
          <button class="transaction-popup__send-now" (click)="sendTransactionNow()">
            <mat-icon>point_of_sale</mat-icon> Zarejestruj teraz
          </button>
          <button class="transaction-popup__cancel" (click)="cancelTransactionPopup()">
            <mat-icon>close</mat-icon> Anuluj
          </button>
        }
        <!-- Sending phase -->
        @if (transactionProcessing) {
          <mat-spinner diameter="64"></mat-spinner>
          <p class="transaction-popup__subtitle">Rejestrowanie&#8230;</p>
        }
        <!-- Success phase -->
        @if (transactionSuccess) {
          <mat-icon class="transaction-popup__result-icon transaction-popup__result-icon--ok">check_circle</mat-icon>
          <p class="transaction-popup__subtitle">Transakcja zarejestrowana!</p>
          <button class="transaction-popup__cancel" (click)="cancelTransactionPopup()">
            <mat-icon>close</mat-icon> Zamknij
          </button>
        }
        <!-- Error phase -->
        @if (transactionError) {
          <mat-icon class="transaction-popup__result-icon transaction-popup__result-icon--err">error_outline</mat-icon>
          <p class="transaction-popup__subtitle">{{ transactionError }}</p>
          @if (transactionErrorDetail) {
            <p class="transaction-popup__error-detail">{{ transactionErrorDetail }}</p>
          }
          <button class="transaction-popup__cancel" (click)="cancelTransactionPopup()">
            <mat-icon>close</mat-icon> Zamknij
          </button>
        }
      </div>
    </div>
  }

  <!-- Product registration popup -->
  @if (productPopupVisible) {
    <div class="entry-popup-overlay">
      <div class="entry-popup">
        <!-- Countdown phase -->
        @if (!productProcessing && !productSuccess && !productError) {
          <p class="entry-popup__name">{{ scanDetails?.person_name }}</p>
          <p class="entry-popup__subtitle">Rejestracja nagrody:</p>
          <p class="entry-popup__name">{{ selectedProduct?.name }}</p>
          <div class="entry-popup__ring">
            <svg viewBox="0 0 120 120" width="120" height="120">
              <circle class="ring-bg" cx="60" cy="60" r="54"/>
              <circle class="ring-progress" cx="60" cy="60" r="54"/>
            </svg>
            <span class="entry-popup__number">{{ productCountdown }}</span>
          </div>
          <button class="entry-popup__send-now" (click)="sendProductNow()">
            <mat-icon>card_giftcard</mat-icon> Zarejestruj teraz
          </button>
          <button class="entry-popup__cancel" (click)="cancelProductPopup()">
            <mat-icon>close</mat-icon> Anuluj
          </button>
        }
        <!-- Sending phase -->
        @if (productProcessing) {
          <mat-spinner diameter="64"></mat-spinner>
          <p class="entry-popup__subtitle">Rejestrowanie&#8230;</p>
        }
        <!-- Success phase -->
        @if (productSuccess) {
          <mat-icon class="entry-popup__result-icon entry-popup__result-icon--ok">check_circle</mat-icon>
          <p class="entry-popup__subtitle">Nagroda zarejestrowana!</p>
          <button class="entry-popup__cancel" (click)="cancelProductPopup()">
            <mat-icon>close</mat-icon> Zamknij
          </button>
        }
        <!-- Error phase -->
        @if (productError) {
          <mat-icon class="entry-popup__result-icon entry-popup__result-icon--err">error_outline</mat-icon>
          <p class="entry-popup__subtitle">{{ productError }}</p>
          <button class="entry-popup__cancel" (click)="cancelProductPopup()">
            <mat-icon>close</mat-icon> Zamknij
          </button>
        }
      </div>
    </div>
  }

  <!-- Entry registration popup -->
  @if (entryPopupVisible) {
    <div class="entry-popup-overlay">
      <div class="entry-popup">
        <!-- Countdown phase -->
        @if (!entryProcessing && !entrySuccess && !entryError) {
          <p class="entry-popup__name">{{ scanDetails?.person_name }}</p>
          <p class="entry-popup__subtitle">Rejestracja wej&#347;cia za:</p>
          <div class="entry-popup__ring">
            <svg viewBox="0 0 120 120" width="120" height="120">
              <circle class="ring-bg" cx="60" cy="60" r="54"/>
              <circle class="ring-progress" cx="60" cy="60" r="54"/>
            </svg>
            <span class="entry-popup__number">{{ entryCountdown }}</span>
          </div>
          <button class="entry-popup__send-now" (click)="sendEntryNow()">
            <mat-icon>how_to_reg</mat-icon> Zarejestruj teraz
          </button>
          <button class="entry-popup__cancel" (click)="cancelEntryPopup()">
            <mat-icon>close</mat-icon> Anuluj
          </button>
        }
        <!-- Sending phase -->
        @if (entryProcessing) {
          <mat-spinner diameter="64"></mat-spinner>
          <p class="entry-popup__subtitle">Rejestrowanie&#8230;</p>
        }
        <!-- Success phase -->
        @if (entrySuccess) {
          <mat-icon class="entry-popup__result-icon entry-popup__result-icon--ok">check_circle</mat-icon>
          <p class="entry-popup__subtitle">Wej&#347;cie zarejestrowane!</p>
          <button class="entry-popup__cancel" (click)="cancelEntryPopup()">
            <mat-icon>close</mat-icon> Zamknij
          </button>
        }
        <!-- Error phase -->
        @if (entryError) {
          <mat-icon class="entry-popup__result-icon entry-popup__result-icon--err">error_outline</mat-icon>
          <p class="entry-popup__subtitle">{{ entryError }}</p>
          <button class="entry-popup__cancel" (click)="cancelEntryPopup()">
            <mat-icon>close</mat-icon> Zamknij
          </button>
        }
      </div>
    </div>
  }

</div>